import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Zap, Leaf } from "lucide-react";
import { co2SavedKg, co2Trees } from "../lib/co2";
import { api } from "../lib/api";
import { useGuestLoad } from "../lib/useGuestLoad";
import { GuestRetryScreen } from "../components/GuestRetryScreen";
import { TripMap } from "../components/TripMap";
import { usePageMeta } from "../lib/seo";
import { Timeline } from "../components/Timeline";
import { TripStory } from "../components/TripStory";
import { VehicleCard } from "../components/VehicleCard";
import { PosterDialog } from "../components/PosterDialog";
import { DrivingNowBanner } from "../components/DrivingNowBanner";
import { LangSwitcher } from "../components/LangSwitcher";
import { useJournalLang } from "../lib/useJournalLang";
import { toast } from "sonner";
import { makeT } from "../lib/journalI18n";

export default function SharePage() {
  const { token } = useParams();
  const { data: trip, error, load } = useGuestLoad(`/share/${token}`);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [liveProgress, setLiveProgress] = useState(null);
  const { lang, setLang, t, locale } = useJournalLang();
  const [tr, setTr] = useState(null);
  const [translating, setTranslating] = useState(false);
  const mapWrapRef = useRef(null);
  useEffect(() => {
    if (lang === "nl" || !trip) { setTr(null); return; }
    let stop = false;
    setTranslating(true);
    api.get(`/share/${token}/translations`, { params: { lang }, timeout: 120000 })
      .then((r) => { if (!stop) setTr(r.data); })
      .catch(() => { if (!stop) toast.error(makeT(lang)("translateFail")); })
      .finally(() => { if (!stop) setTranslating(false); });
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, trip, token]);
  const progress = liveProgress || (trip?.progress?.total_km ? trip.progress : null);
  useEffect(() => {
    if (!trip?.progress?.total_km || trip?.progress?.finished) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/share/${token}/live`);
        if (data?.progress?.total_km) setLiveProgress(data.progress);
      } catch { /* volgende poll */ }
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [trip?.progress?.total_km, trip?.progress?.finished, token]);
  usePageMeta(trip ? `${trip.name} — gedeelde EV-roadtrip | VoltRoute` : undefined, trip ? `Bekijk de elektrische roadtrip "${trip.name}" van ${trip.origin?.name || ""} naar ${trip.destination?.name || ""} — gepland met VoltRoute.` : undefined);

  if (error === "transient" && !trip) return <GuestRetryScreen onRetry={load} />;

  if (error === "invalid") {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center" data-testid="share-not-found">
        <h1 className="font-heading font-extrabold text-2xl mb-3">{t("shareNotFoundTitle")}</h1>
        <p className="text-muted-foreground mb-6">{t("shareNotFoundBody")}</p>
        <Link to="/" className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-6 py-3 text-sm font-semibold transition-[background-color] duration-200">{t("planOwn")}</Link>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="share-page">
      <header className="sticky top-0 z-40 glass-panel border-b border-border/60">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </span>
            <div className="min-w-0">
              <h1 className="font-heading font-extrabold text-lg tracking-tight truncate">{trip.name}</h1>
              <p className="text-xs text-muted-foreground truncate">
                {t("sharedTrip")} · {trip.origin?.name} → {trip.destination?.name}{trip.round_trip ? ` → ${t("back")}` : ""} · <span className="font-mono">{trip.route?.distance_km} km</span>
                {trip.travel_mode !== "moto" && (trip.route?.distance_km || 0) > 0 && (
                  <span data-testid="co2-badge-share" title={t("co2ShareTip")}
                    className="inline-flex items-center gap-1 ml-1.5 text-[#2D6A4F] font-semibold">
                    · <Leaf className="w-3 h-3" /> <span className="font-mono">{co2SavedKg(trip.route.distance_km)} kg</span> {t("co2Saved")}
                  </span>
                )}
                {progress?.source === "live" && (
                  <span data-testid="live-status-chip" className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-600 text-[10px] font-bold px-2 py-0.5 ml-1.5 align-middle">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                    </span>
                    LIVE
                    {progress.position_ts ? ` · ${t("lastSeen", { time: new Date(progress.position_ts).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) })}` : ""}
                    {progress.current_day_number ? ` · ${t("dayOnRoad", { n: progress.current_day_number })}` : ""}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 min-w-0 overflow-x-auto hide-scrollbar [&>*]:shrink-0">
            <LangSwitcher lang={lang} setLang={setLang} translating={translating} small />
            {trip.route && <PosterDialog trip={trip} progress={progress} shareUrl={`${window.location.origin}/share/${token}`} />}
            <Link to="/" data-testid="share-cta-link" className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-5 py-2.5 text-sm font-semibold shrink-0 transition-[background-color] duration-200">
              {t("planOwn")}
            </Link>
          </div>
        </div>
        {(() => {
          const d = (trip.days || []).find((x) => x.day_number === progress?.current_day_number);
          const dayLabel = d ? `${t("day")} ${d.day_number}: ${(d.start?.name || "").split(",")[0]} → ${(d.end?.name || "").split(",")[0]}` : "";
          return (
            <div className="px-4 sm:px-6 pb-3 empty:hidden">
              <DrivingNowBanner progress={progress} dayLabel={dayLabel} t={t} locale={locale}
                onFollow={() => {
                  if (progress?.position) setSelectedPoint({ lat: progress.position.lat, lon: progress.position.lon });
                  mapWrapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }} />
            </div>
          );
        })()}
      </header>
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-64px)]">
        <div ref={mapWrapRef} className="h-[38vh] lg:h-auto lg:flex-1 lg:order-2">
          {trip.route && <TripMap trip={trip} selectedPoint={selectedPoint} dark={false} shareToken={token} progress={progress} t={t} locale={locale} />}
        </div>
        <div className="lg:w-[520px] lg:order-1 overflow-y-auto hide-scrollbar p-4 sm:p-6" data-testid="share-timeline">
          {trip.vehicle && <VehicleCard trip={trip} readOnly shareToken={token} />}
          <Timeline trip={trip} onSelectPoint={setSelectedPoint} readOnly />
          {trip.story && (
            <div className="mt-8">
              <TripStory trip={trip} readOnly shareToken={token} tr={tr} t={t} locale={locale} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
